import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { BlackDuckProjectGroup, User } from "../../../../app/backendTypes";
import {
  useBlackDuckProjectGroupManager,
  useBlackDuckProjectGroupMembership,
  useBlackDuckProjectGroups,
} from "../../../../app/hooks";
import Error from "../../Error";
import Loading from "../../Loading";
import UserSpan from "../../users/UserSpan";
import PendingAction from ".././PendingAction";
import BlackDuckProjectPicker from "./BlackDuckProjectPicker";
import BlackDuckProjectGroupAccessDialog from "./BlackDuckProjectGroupAccessDialog";
import LoadingSpan from "../../LoadingSpan";
import ErrorSpan from "../../ErrorSpan";
import Morselbar from "../../Morselbar";

function BlackProjectsDialog(props: {
  open: boolean;
  setOpen: (val: boolean) => void;
  user: User;
}) {
  const { open, setOpen, user } = props;

  const { projectGroups, loading, error, reload } = useBlackDuckProjectGroups(
    !open
  );

  const [errorOpen, setErrorOpen] = useState(false);

  useEffect(() => {
    if (error) setErrorOpen(true);
  }, [error]);

  const [projectGroup, setProjectGroup] =
    useState<BlackDuckProjectGroup | null>(null);

  useEffect(() => {
    if (projectGroup !== null) {
      const newProject = projectGroups.find((p) => p.id === projectGroup.id);
      if (newProject !== undefined) {
        if (JSON.stringify(newProject) !== JSON.stringify(projectGroup))
          setProjectGroup(newProject);
      } else {
        setProjectGroup(null);
      }
    }
  }, [projectGroup, projectGroups]);

  return (
    <>
      <Dialog open={open} fullWidth>
        <DialogTitle>
          Black Duck Projects
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            onClick={() => {
              setProjectGroup(null);
              setOpen(false);
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BlackDuckProjectPicker
            projectGroups={projectGroups}
            loading={loading}
            error={error}
            onChange={(val) => setProjectGroup(val)}
            initialValue={projectGroup}
          />
          <br />
          <br />
          {projectGroup !== null && (
            <ProjectGroupAccess
              user={user}
              projectGroup={projectGroup}
              callback={reload}
            />
          )}
          <br />
          <DialogContentText>
            For onboarding new projects visit the{" "}
            <Link
              target="_blank"
              href="https://jira.visma.com/servicedesk/customer/portal/8/create/205?customfield_29560=VSC-1565936&customfield_29561=VSC-1565935"
            >
              support portal
            </Link>
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to load projects"
        error={error}
      />
    </>
  );
}

export default BlackProjectsDialog;

function ProjectGroupAccess(props: {
  user: User;
  projectGroup: BlackDuckProjectGroup;
  callback: () => void;
}) {
  const { user, projectGroup, callback } = props;

  const { membership, loading, error, reload } =
    useBlackDuckProjectGroupMembership(user.employee.id, projectGroup.id || "");

  const task = membership?.task;

  useEffect(() => {
    if (
      task !== undefined &&
      task !== null &&
      (task.status.kind === "Done" || task.status.kind === "Cancelled")
    )
      callback();
  }, [callback, task]);

  let element = <></>;
  const actions = [];

  if (loading) {
    actions.push(<Loading />);
  } else if (error) {
    actions.push(<Error error={error} />);
  } else if (task !== undefined && task !== null) {
    actions.push(
      <PendingAction task={task} callback={reload} onCompleted={reload} />
    );
  }

  if (membership !== null) {
    if (membership.is_member)
      element = (
        <Typography>
          <UserSpan user={user} /> is assigned to{" "}
          <Typography component="span" color="secondary">
            {projectGroup.name}
          </Typography>
        </Typography>
      );
    else
      element = (
        <>
          <Typography>
            <UserSpan user={user} /> is not assigned to{" "}
            <Typography component="span" color="secondary">
              {projectGroup.name}
            </Typography>
          </Typography>
          {(membership.task === null || membership.task === undefined) && (
            <>
              <br />
              <RequestAccessButton
                user={user}
                projectGroup={projectGroup}
                reload={reload}
              />
            </>
          )}
        </>
      );
  }

  return (
    <>
      <ProjectGroupManager projectGroup={projectGroup} />
      <br />
      {element}
      <br />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {actions.map((a, i) => (
          <Fragment key={i}>{a}</Fragment>
        ))}
      </Box>
    </>
  );
}

function ProjectGroupManager(props: { projectGroup: BlackDuckProjectGroup }) {
  const { projectGroup } = props;

  const { manager, loading, error } = useBlackDuckProjectGroupManager(
    projectGroup.id || ""
  );

  let element = <></>;
  if (loading) element = <LoadingSpan />;
  else if (error) element = <ErrorSpan error={error} />;
  else if (manager !== null) element = <UserSpan user={manager} />;

  return <Typography>Project group manager is {element}</Typography>;
}

function RequestAccessButton(props: {
  user: User;
  projectGroup: BlackDuckProjectGroup;
  reload: () => void;
}) {
  const { user, projectGroup, reload } = props;

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setOpen(true)} color="secondary">
        Request Access
      </Button>
      <BlackDuckProjectGroupAccessDialog
        open={open}
        setOpen={setOpen}
        user={user}
        projectGroup={projectGroup}
        callback={reload}
      />
    </>
  );
}
